import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type PracticeHistoryExamName = 'Academic' | 'Core';

export type PracticeHistoryAnalysisQuery = {
    fromUtc?: string;
    toUtc: string;
    allTime: boolean;
    examName?: PracticeHistoryExamName;
    thresholds: number[];
    minimumPracticeCount: number;
    isPaid?: boolean;
    search?: string;
    page: number;
    pageSize: number;
    sortBy: 'practiceCount' | 'practiceTimeSeconds' | 'lastPracticeAtUtc' | 'email';
    sortDirection: 'asc' | 'desc';
};

export type PracticeHistorySummary = {
    practicedUsers: number;
    paidUsers: number;
    practiceAttempts: number;
    practiceTimeSeconds: number;
    averagePracticesPerUser: number;
};

export type PracticeHistoryThreshold = {
    threshold: number;
    userCount: number;
    paidUserCount: number;
};

export type PracticeHistoryUser = {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    emailVerified: boolean;
    isDeleted: boolean;
    isPaid: boolean;
    practiceCount: number;
    practiceTimeSeconds: number;
    firstPracticeAtUtc: string;
    lastPracticeAtUtc: string;
};

export type PracticeHistoryAnalysisData = {
    generatedAtUtc: string;
    fromUtc: string | null;
    toUtc: string;
    examName: PracticeHistoryExamName | null;
    thresholds: number[];
    summary: PracticeHistorySummary;
    thresholdAnalysis: PracticeHistoryThreshold[];
    users: {
        page: number;
        pageSize: number;
        totalCount: number;
        items: PracticeHistoryUser[];
    };
};

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
}

function hasVersionedApiPath(value: string) {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asNumber(value: unknown) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asString(value: unknown) {
    return typeof value === 'string' ? value : '';
}

function asBoolean(value: unknown) {
    return value === true;
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const candidate = record.errorMessage ?? record.message ?? record.error ?? record.title ?? record.detail;
    return typeof candidate === 'string' && candidate.trim() ? candidate : fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Practice history analysis request failed'));
    }

    return envelope.data ?? (payload as T);
}

function normalizeUser(value: unknown): PracticeHistoryUser {
    const record = asRecord(value);
    return {
        userId: asString(record.userId),
        firstName: asString(record.firstName),
        lastName: asString(record.lastName),
        email: asString(record.email),
        emailVerified: asBoolean(record.emailVerified),
        isDeleted: asBoolean(record.isDeleted),
        isPaid: asBoolean(record.isPaid),
        practiceCount: asNumber(record.practiceCount),
        practiceTimeSeconds: asNumber(record.practiceTimeSeconds),
        firstPracticeAtUtc: asString(record.firstPracticeAtUtc),
        lastPracticeAtUtc: asString(record.lastPracticeAtUtc),
    };
}

function normalizeData(value: unknown): PracticeHistoryAnalysisData {
    const record = asRecord(value);
    const summary = asRecord(record.summary);
    const users = asRecord(record.users);
    const examName = asString(record.examName);

    return {
        generatedAtUtc: asString(record.generatedAtUtc),
        fromUtc: record.fromUtc === null ? null : asString(record.fromUtc),
        toUtc: asString(record.toUtc),
        examName: examName === 'Academic' || examName === 'Core' ? examName : null,
        thresholds: Array.isArray(record.thresholds) ? record.thresholds.map(asNumber) : [],
        summary: {
            practicedUsers: asNumber(summary.practicedUsers),
            paidUsers: asNumber(summary.paidUsers),
            practiceAttempts: asNumber(summary.practiceAttempts),
            practiceTimeSeconds: asNumber(summary.practiceTimeSeconds),
            averagePracticesPerUser: asNumber(summary.averagePracticesPerUser),
        },
        thresholdAnalysis: Array.isArray(record.thresholdAnalysis)
            ? record.thresholdAnalysis.map((item) => {
                  const threshold = asRecord(item);
                  return {
                      threshold: asNumber(threshold.threshold),
                      userCount: asNumber(threshold.userCount),
                      paidUserCount: asNumber(threshold.paidUserCount),
                  };
              })
            : [],
        users: {
            page: asNumber(users.page) || 1,
            pageSize: asNumber(users.pageSize) || 25,
            totalCount: asNumber(users.totalCount),
            items: Array.isArray(users.items) ? users.items.map(normalizeUser) : [],
        },
    };
}

export async function getPracticeHistoryAnalysis(query: PracticeHistoryAnalysisQuery) {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminPracticeHistoryAnalysis`);

    if (query.fromUtc) url.searchParams.set('fromUtc', query.fromUtc);
    url.searchParams.set('toUtc', query.toUtc);
    url.searchParams.set('allTime', String(query.allTime));
    if (query.examName) url.searchParams.set('examName', query.examName);
    query.thresholds.forEach((threshold) => url.searchParams.append('thresholds', String(threshold)));
    url.searchParams.set('minimumPracticeCount', String(query.minimumPracticeCount));
    if (query.isPaid !== undefined) url.searchParams.set('isPaid', String(query.isPaid));
    if (query.search) url.searchParams.set('search', query.search);
    url.searchParams.set('page', String(query.page));
    url.searchParams.set('pageSize', String(query.pageSize));
    url.searchParams.set('sortBy', query.sortBy);
    url.searchParams.set('sortDirection', query.sortDirection);

    const headers = new Headers({ Accept: 'application/json' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);

    const response = await fetch(url.toString(), { method: 'GET', headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return normalizeData(
        unwrapAdminApiPayload(payload as PracticeHistoryAnalysisData | AdminApiEnvelope<PracticeHistoryAnalysisData>),
    );
}
